import {AfterViewInit, Component, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {View} from "src/app/common/view";
import {Handler} from "src/app/common/handler";
import {AppContext} from "../../../app-context";
import {map, Observable, of} from "rxjs";
import {
    AlertStatus,
    AlertType,
    FacetFilter,
    FacetResult,
    FacetValueResult,
    GetMyAlertsNew,
    Organisation,
    UserProfile
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {filterByTermArray, removeItem, sort} from "../../../common/utils";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {ComparatorChain} from "../../../common/comparator-chain";
import {DateFieldRange, MomentDateFieldRange} from "../../../common/date/date-range/date-field-range";
import {FacetUtils, GetFacetValues} from "../../../common/facets/facet-utils";
import {sendFacetsQuery} from "../../../common/app-common-utils";
import {AlertEntity} from "../../../handlers/issues-standalone-handler";
import {HandleCommand} from "../../../common/handle-command";
import {AddAlertComponent} from "../add-alert/add-alert.component";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";
import {Entity} from "../../../handlers/entity";
import {HandleQuery} from "../../../common/handle-query";
import {DashboardContext} from "../../dashboard/dashboard.context";
import {downloadAlertsAsExcel} from "../alerts-list-excel";
import moment from "moment";

@Component({
    selector: 'app-alerts-overview',
    templateUrl: './alerts-overview.component.html',
    styleUrls: ['./alerts-overview.component.scss']
})
@Handler()
export class AlertsOverviewComponent extends View implements AfterViewInit, OnInit {
    private route: ActivatedRoute = inject(ActivatedRoute);
    facetUtils = FacetUtils;
    comparator: ComparatorChain = new ComparatorChain('alertStatusPriority', '-alert.timestamp',
        'entity.connection.info.code', 'alert.alertId');
    appContext = AppContext;

    term: string;
    query: Observable<AlertEntity[]>;
    data: AlertEntity[] = [];
    ranges: MomentDateFieldRange[];
    hiddenFacets: string[] = ["entityIds"];
    facets: Observable<FacetResult>;
    facetFilters: FacetFilter[];
    facetsLocalStorageKey: string | null = "alerts-overview-facets";
    dateRangeLocalStorageKey: string | null = "alerts-overview-range";
    defaultFacetFilters: FacetFilter[] = [{
        facetName: "alertStatus",
        values: ["OPEN"]
    }];
    @ViewChild("filtersInModal") filtersTemplate: TemplateRef<any>;

    downloadAlertsAsExcel = () => this.sendQuery("getAlerts", this.getDataQuery()).subscribe(a => downloadAlertsAsExcel(a));

    selecting: boolean;
    selectedItems: AlertEntity[] = [];
    organisations: Organisation[] = [];
    users: Map<string, UserProfile> = new Map();

    ngOnInit() {
        this.applyNotificationFilters();
        this.subscribeTo("searchUsers").subscribe(u => {
            this.users = new Map(u.map(user => [user.userId, user]));
        });
    }

    ngAfterViewInit() {
        this.subscribeTo("getOrganisations").subscribe(o => this.organisations = o);
        this.sendQuery("getTimeRanges").subscribe(r => this.ranges = r);
        this.executeQuery();
    }

    executeQuery = () => {
        const query = this.getDataQuery();
        this.query = this.subscribeTo("getAlerts", query)
            .pipe(map((o: AlertEntity[]) => o.filter(filterByTermArray([this.term], Entity.getExcludedFilterFields()))))
            .pipe(map(c => sort(c, this.comparator)));
        this.facets = this.getFacets(query);
    }

    private getFacets(query: GetMyAlertsNew) {
        return sendFacetsQuery("com.flowmaps.api.monitoring.alerting.GetMyAlertsNew", query, {caching: false});
    }

    private getDataQuery() {
        return <GetMyAlertsNew>{facetFilters: this.facetFilters, timeRange: this.dateRange};
    }

    private applyNotificationFilters() {
        if (!this.isEdsnDisconnectedNotification()) {
            return;
        }
        this.facetsLocalStorageKey = null;
        this.dateRangeLocalStorageKey = null;
        this.facetFilters = [
            {
                facetName: "alertStatus",
                values: [AlertStatus.OPEN]
            },
            {
                facetName: "type",
                values: [AlertType.Disconnected]
            },
            {
                facetName: "surveyor",
                values: ["EDSN"]
            }
        ];
        this.dateRange = {
            start: moment().subtract(7, "days").toISOString(),
            end: moment().toISOString(),
            label: "Past week"
        };
    }

    private isEdsnDisconnectedNotification = (): boolean =>
        this.route.snapshot.queryParamMap.get("notification") === "edsn-disconnected";

    trackByForRecord = (index: number, record: AlertEntity) => record.alert.alertId;

    openFiltersInModal = () => this.openModal(this.filtersTemplate, null);

    addAlert = () => this.openModal(AddAlertComponent);

    facetNameFormatter = (name: string): string => {
        switch (name) {
            case "alertStatus":
                return "Alert status";
            case "type":
                return "Alert type";
            case "organisationId":
                return "Organisation";
            case "surveyor":
                return "Data source";
            case "assigneeId":
                return "Assigned to";
            default:
                return SentenceCasePipe.formatWithSpaces(name);
        }
    }

    getTranslateValues = (name: string): boolean => {
        switch (name) {
            case "organisationId":
            case "entityIds":
            case "message":
            case "surveyor":
            case "assigneeId":
                return false;
            default:
                return true;
        }
    }

    getFacetValueFormatter = (name: string): (value: string) => string => {
        switch (name) {
            case "alertStatus":
                return v => RefdataUtils.enumFormatter(v);
            case "organisationId":
                return v => RefdataUtils.organisationFormatter(this.organisations.find(o => o.organisationId === v));
            case "assigneeId":
                return v => {
                    const user = this.users.get(v);
                    return user ? RefdataUtils.userFormatter(user) : v;
                };
        }
    }

    allItemsSelected = (): boolean => this.selectedItems.length === this.data.length;

    toggleSelectAll = (): void => {
        const allSelected = this.allItemsSelected();
        this.data.forEach(d => this.selectionChange({
            entity: d,
            selected: !allSelected
        }));
    }

    toggleSelection = () => {
        this.selecting = !this.selecting;
        if (!this.selecting) {
            this.selectedItems.forEach(c => delete c['selected']);
            this.selectedItems = [];
        }
    }

    @HandleCommand("selectionChange")
    selectionChange(record: { entity: AlertEntity, selected: boolean }) {
        if (record.selected) {
            if (!this.selectedItems.includes(record.entity)) {
                this.selectedItems.push(record.entity);
            }
        } else {
            this.selectedItems = removeItem(this.selectedItems, record.entity);
        }
        record.entity['selected'] = record.selected;
    }

    @HandleQuery("getTimeRanges")
    getTimeRanges(): Observable<MomentDateFieldRange[]> {
        return of(Object.values(DashboardContext.ranges));
    }

    static defaultRange = (): DateFieldRange => {
        return {
            start: null,
            end: null,
            label: "All time"
        };
    };

    dateRange: DateFieldRange = AlertsOverviewComponent.defaultRange();

    @HandleQuery("getFacetValues", { global: true })
    getFacetValues(query: GetFacetValues): Observable<FacetValueResult[]> {
        if (Object.keys(this.facetFilters).length) {
            const dataQuery = this.getDataQuery();
            return FacetUtils.getFacetValues(dataQuery, this.getFacets, query.facetName);
        }
        return of(query.allFacets.find(f => f.name === query.facetName).values);
    }
}
